import styled, { css } from 'styled-components';

import * as Q from './TaskQuick.styled';
import { BUCKET_TONES, chipTone, ITaskTone, SURFACE } from './taskTones';

/* Цвета — из `taskTones.ts`, общего с доской и Гантом. Пока этот файл держал
 * свои литералы, высокий приоритет был здесь ЯНТАРНЫМ, а на карточке доски —
 * красным; «в работе» — #1d4ed8 здесь и #1e3a8a там. */

export const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const List = styled.div<{ $fill?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 14px;

  ${({ $fill }) =>
    $fill &&
    css`
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      padding-right: 4px;
    `}
`;

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #475569;
`;

export const SectionToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  border: 0;
  background: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #475569;

  &:hover {
    color: #0f172a;
  }
`;

export const SectionCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  background: #f1f5f9;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0;
`;

export const SectionNote = styled.span`
  font-size: 11px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  color: #94a3b8;
`;

export const GroupHead = styled.div<{ $tone: ITaskTone }>`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${({ $tone }) => $tone.fg};
`;

export const Rows = styled.ul<{ over?: boolean }>`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-radius: 8px;
  min-height: 8px;
  background: ${({ over }) => (over ? '#eff6ff' : 'transparent')};
  transition: background 0.12s;
`;

/* --- The row -------------------------------------------------------------
 * Handle, checkbox and title are SIBLINGS — never nested. The title button
 * stretches over the whole row via ::after, so clicking anywhere but the two
 * controls opens the details drawer. */

/**
 * Ручка перетаскивания.
 *
 * ТЗ 0063: раньше это была бледная иконка в 20px цветом `#cbd5e1` — владелец
 * решил, что перетаскивание вообще не работает, хотя оно работало: он просто
 * не попадал в цель. Ручка осталась ОТДЕЛЬНЫМ элементом (корпус строки тянуть
 * нельзя: react-beautiful-dnd отказывается начинать перетаскивание с вложенных
 * контролов, и ровно из-за этого её когда-то и вынесли), но стала крупнее,
 * контрастнее и с собственным фоном при наведении.
 */
export const DragHandle = styled.button`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 28px;
  border: 0;
  border-radius: 6px;
  background: none;
  padding: 0;
  cursor: grab;
  color: #94a3b8;
  transition: color 0.12s ease, background 0.12s ease;

  &:hover,
  &:focus-visible {
    color: #1d4ed8;
    background: #eff6ff;
  }

  &:active {
    cursor: grabbing;
  }
`;

export const Row = styled.li<{ $done?: boolean; $dragging?: boolean }>`
  position: relative;
  display: grid;
  /* Первая колонка — под ручку 24px (ТЗ 0063). У названия есть НИЖНИЙ предел:
     ТЗ 0065 положило в правую колонку до трёх чипов связей и полосу быстрых
     действий, а трек auto сайзится по содержимому — название сжималось
     вплоть до нуля, и от строки оставались одни чипы. */
  grid-template-columns: 24px 18px minmax(120px, 1fr) minmax(0, auto);
  align-items: center;
  gap: 8px;
  padding: 7px 10px 7px 2px;
  border: 1px solid ${({ $dragging }) => ($dragging ? '#93c5fd' : '#e2e8f0')};
  border-radius: 8px;
  background: ${({ $dragging }) => ($dragging ? '#eff6ff' : '#fff')};
  box-shadow: ${({ $dragging }) =>
    $dragging ? '0 6px 18px rgba(15, 23, 42, 0.12)' : 'none'};

  &:hover {
    border-color: #cbd5e1;
  }

  /* Строка при наведении сама показывает, что её можно тянуть. */
  &:hover ${DragHandle} {
    color: #475569;
  }

  &:hover ${Q.Actions}, &:focus-within ${Q.Actions} {
    visibility: visible;
    opacity: 1;
  }

  ${({ $done }) =>
    $done &&
    css`
      opacity: 0.62;
    `}
`;

export const RowCheck = styled.input`
  position: relative;
  z-index: 1;
  width: 16px;
  height: 16px;
  margin: 0;
  cursor: pointer;
  accent-color: #2563eb;
`;

export const RowTitle = styled.button<{ $done?: boolean }>`
  border: 0;
  background: none;
  padding: 0;
  text-align: left;
  font: inherit;
  font-size: 13px;
  color: #0f172a;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-decoration: ${({ $done }) => ($done ? 'line-through' : 'none')};

  /* Stretched link: the whole row is the hit area, but there is exactly ONE
     button here — no nested interactive elements. */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 8px;
  }

  &:focus-visible::after {
    outline: 2px solid #2563eb;
    outline-offset: -2px;
  }
`;

export const RowMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
`;

export const OverdueTag = styled.span`
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 6px;
  border-radius: 4px;
  background: ${BUCKET_TONES.overdue.bg};
  color: ${BUCKET_TONES.overdue.fg};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
`;

export const RowDue = styled.span<{ $overdue?: boolean }>`
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  /* Поле fg, а не line — то же самое, что у карточки доски. Разъехались они
     МОЛЧА и сразу: файл тонов завели, чтобы один признак не был покрашен
     дважды, и в том же коммите просрочка стала #b91c1c на доске и #dc2626 в
     списке. Тест тонов теперь сверяет ВЫЧИСЛЕННЫЙ цвет обоих элементов, а не
     содержимое карт. (Обратные кавычки внутри styled-шаблона обрывают его.) */
  color: ${({ $overdue }) =>
    $overdue ? BUCKET_TONES.overdue.fg : SURFACE.muted};
`;

export const RowChip = styled.span<{ tone?: 'high' | 'low' | 'accent' }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  height: 18px;
  padding: 0 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ tone }) => chipTone(tone).bg};
  color: ${({ tone }) => chipTone(tone).fg};
`;

export const Empty = styled.div`
  font-size: 13px;
  color: #94a3b8;
  padding: 10px 2px;
`;

/* --- Quick add ----------------------------------------------------------- */

export const QuickAdd = styled.div<{ $compact?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  /* В списке строка выровнена по названиям (отступ под ручку и чекбокс);
     в колонке доски она стоит на месте карточки и отступа не имеет. */
  padding: ${({ $compact }) => ($compact ? '0' : '4px 10px 4px 30px')};
`;

export const QuickAddInput = styled.input`
  flex: 1;
  min-width: 0;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  padding: 7px 10px;
  font: inherit;
  font-size: 13px;
  background: transparent;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    border-style: solid;
    border-color: #2563eb;
    background: #fff;
    outline: none;
  }
`;

/* --- Toolbar ------------------------------------------------------------- */

export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  /* 12 → 8px: строка фильтров и полотно задач — соседи, а не разделы. */
  margin-bottom: 8px;
`;

export const Spacer = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ChipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const FilterChip = styled.button<{ active?: boolean }>`
  border: 1px solid ${({ active }) => (active ? '#2563eb' : '#e2e8f0')};
  background: ${({ active }) => (active ? '#eff6ff' : '#fff')};
  color: ${({ active }) => (active ? '#1d4ed8' : '#475569')};
  border-radius: 999px;
  padding: 4px 10px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #cbd5e1;
  }

  /* Недоступный чип должен ВЫГЛЯДЕТЬ недоступным: иначе «не нажимается»
     читается как поломка, а не как запрет. */
  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

/* --- Deadline board: collapsible sections below 1280px ------------------- */

export const StackHead = styled.button<{ $tone: ITaskTone }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 0;
  background: none;
  padding: 8px 4px;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ $tone }) => $tone.fg};
`;

/* --- Поповер фильтров (ТЗ 0062) ------------------------------------------ */

export const FilterPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 380px;
`;

export const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

/** Постоянная подсказка под контролом — не подсказка по наведению.
 *  Правило «внутри вида — любой, между видами — одновременно» неочевидно, и
 *  узнавать его, наводя мышь, человек не станет. */
export const FilterHint = styled.div`
  margin-top: 6px;
  font-size: 11px;
  line-height: 1.4;
  color: #94a3b8;
`;

export const FilterGroupLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #94a3b8;
`;
